// Centralized API URL helper
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.careerstar.co';

export function apiUrl(path = '') {
  if (!path) return API_BASE;
  const needsSlash = !path.startsWith('/');
  return `${API_BASE}${needsSlash ? '/' : ''}${path}`;
}

export default apiUrl;


// Open a file that lives behind the API.
//
// Uploaded files are served by the app now, not from a public S3 URL, so their links are
// API paths like /api/section/44/syllabus/download. Two things break if such a path is put
// straight into an <a href>:
//
//   1. The browser resolves it against the SITE, so in development it hits the React dev
//      server on :3000 rather than the API on :3001, and CRA's proxy deliberately does not
//      forward requests that accept HTML - so the router answers with its own 404 page.
//   2. A plain navigation is not fetch, so installAuth's token never goes with it and the
//      route replies 401.
//
// Fetching it here solves both: apiUrl points at the right host, the patched fetch attaches
// the token, and the response becomes a blob the browser can open or download.
// What a browser can actually render, which is what the backend already serves inline.
// An Office file cannot be previewed by any browser: it has to be converted first.
export const PREVIEWABLE = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];

// Canvas stops previewing at roughly this size and makes the file download instead. One real
// submission here is 92 MB, so a ceiling is needed or the pane sits there loading forever.
export const PREVIEW_MAX_BYTES = 30 * 1024 * 1024;

export const fileExt = (name) => {
  const m = /\.([A-Za-z0-9]{1,5})(?:\?|$)/.exec(String(name || ''));
  return m ? m[1].toLowerCase() : '';
};

/** The filename the SERVER chose, read off Content-Disposition. Going through a blob URL
 *  loses it, which is why Office files downloaded under a blob UUID. */
const nameFromHeaders = (res, target) => {
  const cd = res.headers.get('content-disposition') || '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) { try { return decodeURIComponent(star[1]); } catch (e) { /* fall through */ } }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  if (plain) return plain[1];
  try { return decodeURIComponent(target.split('/').pop() || ''); } catch (e) { return ''; }
};

/** Fetch one authorised file and hand back the blob plus what the server said about it.
 *  Shared by openApiFile and by the grading pane, so both use the same authorised route. */
export async function fetchApiFile(url) {
  if (!url) return null;
  const target = /^https?:\/\//i.test(url) ? url : apiUrl(url);
  const res = await fetch(target);
  if (!res.ok) throw new Error(String(res.status));
  const blob = await res.blob();
  return { blob, filename: nameFromHeaders(res, target), contentType: blob.type || '', target };
}

export async function openApiFile(url, { download = false, filename } = {}) {
  if (!url) return;
  let got;
  try {
    got = await fetchApiFile(url);
  } catch (e) {
    console.error('Could not open file:', url, e);
    // eslint-disable-next-line no-alert
    alert('That file could not be opened. Please try again.');
    return;
  }
  const { blob, filename: served } = got;
  const name = filename || served || 'download';
  const href = URL.createObjectURL(blob);
  // An Office file is going to download whatever we do, so name it properly rather than
  // letting the browser invent one from the blob URL.
  const mustDownload = download || !PREVIEWABLE.includes(fileExt(name));
  if (mustDownload) {
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
  // Give the tab time to take the blob before releasing it.
  setTimeout(() => URL.revokeObjectURL(href), 60000);
}
